import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  MargensCustoAtualizadoPraticadosResponse, MargensCustoAtualizadoPropostasResponse, MargensEvolucaoResponse, MargensGrupoResponse,
  MargensItensAbaixoResponse, MargensOrcamentosResponse, MargensResumoResponse,
} from '@triade/contracts';
import { ApiError, requisitar, type Query } from '@/lib/api';
import { useSessao } from '@/lib/session';

/**
 * Painel de margens: cada bloco e um GET /margens/* (agregados no servidor). A chave leva a empresa; trocar filtro ou
 * ordem busca de novo e mantem o resultado anterior na tela ate o novo chegar.
 */
function useMargens<T>(caminho: string, query: Query, ativo = true) {
  const { empresaId } = useSessao();
  return useQuery<T, ApiError>({
    queryKey: ['margens', caminho, empresaId, query],
    queryFn: () => requisitar<T>(`/margens/${caminho}`, { query }),
    enabled: ativo && empresaId !== null,
    placeholderData: keepPreviousData,
  });
}

export const useMargensResumo = (q: Query) => useMargens<MargensResumoResponse>('resumo', q);
export const useMargensOrcamentos = (q: Query) => useMargens<MargensOrcamentosResponse>('orcamentos', q);
export const useMargensEvolucao = (q: Query) => useMargens<MargensEvolucaoResponse>('evolucao', q);
export const useMargensItensAbaixo = (q: Query) => useMargens<MargensItensAbaixoResponse>('itens-abaixo', q);

export type RecorteMargens = 'por-tabela' | 'por-cliente' | 'por-produto' | 'por-categoria' | 'por-tipo';
export const useMargensGrupo = (recorte: RecorteMargens, q: Query) => useMargens<MargensGrupoResponse>(recorte, q);

/**
 * Custo na epoca x hoje (E12, painel de margens). Exigem `margens.ver` e `custo.ver` na API: quem nao tem (VENDEDOR, LEITURA,
 * PCP, RH) recebe 403, entao a tela nao chama (`ativo` = false). Os filtros sao so periodo, tabela e tipo.
 */
export const useMargensCustoPropostas = (q: Query, ativo = true) =>
  useMargens<MargensCustoAtualizadoPropostasResponse>('custo-atualizado/propostas', q, ativo);
export const useMargensCustoPraticados = (q: Query, ativo = true) =>
  useMargens<MargensCustoAtualizadoPraticadosResponse>('custo-atualizado/praticados', q, ativo);
